import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  ShieldCheck, ShieldAlert, ShieldX, Info,
  ExternalLink, Clock, User as UserIcon, ChevronRight,
  CheckCircle2, XCircle, Ban, MessageSquare, RotateCcw,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Zita — Third-Party App Approval panel (Super Admin only)
// Feb 2026 Security Doctrine: recognized apps auto-approve; unknown apps
// enter Pending Approval, Ari reviews, Super Admin has final authority.
// Every decision is written to an immutable audit log.
// ---------------------------------------------------------------------------

const STATUS_STYLE = {
  pending: { icon: Clock, label: 'Pending', tone: 'amber' },
  info_requested: { icon: MessageSquare, label: 'Info requested', tone: 'blue' },
  approved: { icon: CheckCircle2, label: 'Approved', tone: 'emerald' },
  approved_recognized: { icon: ShieldCheck, label: 'Recognized', tone: 'emerald' },
  rejected: { icon: XCircle, label: 'Rejected', tone: 'red' },
  blocked: { icon: Ban, label: 'Blocked', tone: 'red' },
};

const RISK_TONE = {
  low: 'emerald',
  moderate: 'amber',
  high: 'red',
  critical: 'red',
  unknown: 'zinc',
};

const toneClass = (tone) => {
  switch (tone) {
    case 'amber': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'blue': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'emerald': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'red': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-zinc-100 text-zinc-800 border-zinc-200';
  }
};

const Pill = ({ tone = 'zinc', children, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${toneClass(tone)}`}
  >
    {children}
  </span>
);

const RiskBadge = ({ level }) => {
  const tone = RISK_TONE[level] || 'zinc';
  return <Pill tone={tone} testId={`risk-${level}`}>Risk: {String(level).toUpperCase()}</Pill>;
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const Icon = s.icon;
  return (
    <Pill tone={s.tone} testId={`status-${status}`}>
      <Icon size={12} />
      {s.label}
    </Pill>
  );
};

// ---------------------------------------------------------------------------
// Decision Dialog
// ---------------------------------------------------------------------------

const DecisionDialog = ({ open, action, appName, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);

  if (!open) return null;

  const meta = {
    approve: {
      title: `Approve ${appName}`,
      hint: 'Explain briefly why this app is safe to connect. This will be logged.',
      cta: 'Approve app',
      cta_testid: 'tpa-dialog-approve',
      cta_class: 'zita-btn-primary',
    },
    reject: {
      title: `Reject ${appName}`,
      hint: 'Explain the reason for rejection. The user can request again later.',
      cta: 'Reject app',
      cta_testid: 'tpa-dialog-reject',
      cta_class: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded',
    },
    block: {
      title: `Permanently block ${appName}`,
      hint: 'Block reason. The app cannot be re-requested without reopening.',
      cta: 'Block app',
      cta_testid: 'tpa-dialog-block',
      cta_class: 'bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded',
    },
    request_info: {
      title: `Request more information about ${appName}`,
      hint: 'Type the questions you want the user to answer.',
      cta: 'Send request',
      cta_testid: 'tpa-dialog-request-info',
      cta_class: 'bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded',
    },
    reopen: {
      title: `Reopen ${appName} for review`,
      hint: 'Note why you are moving this back to Pending.',
      cta: 'Reopen',
      cta_testid: 'tpa-dialog-reopen',
      cta_class: 'zita-btn-secondary',
    },
  }[action] || { title: '', hint: '', cta: 'OK' };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      data-testid="tpa-decision-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
        <h2 className="font-serif text-2xl text-zita-espresso mb-1">{meta.title}</h2>
        <div className="text-sm text-zita-mocha mb-4">{meta.hint}</div>
        <textarea
          className="zita-input w-full min-h-[120px] mb-4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Type your reason…"
          data-testid="tpa-dialog-textarea"
        />
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            className="text-sm text-zita-mocha px-3 py-2 hover:underline"
            onClick={onClose}
            data-testid="tpa-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!reason.trim()}
            onClick={() => onSubmit(reason.trim())}
            data-testid={meta.cta_testid}
            className={`${meta.cta_class} disabled:opacity-40`}
          >
            {meta.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail Drawer
// ---------------------------------------------------------------------------

const DetailDrawer = ({ appId, onClose, onChanged }) => {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);

  useEffect(() => {
    if (!appId) return;
    setLoading(true);
    api.get(`/admin/third-party-apps/${appId}`)
      .then((r) => setApp(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load app'))
      .finally(() => setLoading(false));
  }, [appId]);

  const decide = async (verb, reason) => {
    try {
      const path = verb === 'request_info'
        ? `/admin/third-party-apps/${appId}/request-info`
        : `/admin/third-party-apps/${appId}/${verb}`;
      const body = verb === 'request_info' ? { message: reason } : { reason };
      const { data } = await api.post(path, body);
      setApp(data);
      setAction(null);
      onChanged?.();
      toast.success(`Decision recorded (${verb.replace('_', ' ')}).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  if (!appId) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-40 w-full max-w-2xl bg-white shadow-2xl overflow-y-auto"
        data-testid="tpa-detail-drawer"
      >
        <div className="p-6 border-b border-zita-taupe/60 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-zita-mocha mb-1">
              Third-Party App
            </div>
            <h2 className="font-serif text-3xl text-zita-espresso">
              {app?.app_name || (loading ? 'Loading…' : 'Unknown app')}
            </h2>
            {app && (
              <div className="mt-2 flex flex-wrap gap-2">
                <StatusBadge status={app.status} />
                {app.ari_review && <RiskBadge level={app.ari_review.risk_level} />}
                {app.is_recognized && (
                  <Pill tone="emerald" testId="tpa-recognized">
                    <ShieldCheck size={12} /> On approved-list
                  </Pill>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zita-mocha hover:text-zita-espresso text-sm"
            data-testid="tpa-drawer-close"
          >
            Close
          </button>
        </div>

        {loading || !app ? (
          <div className="p-6 text-sm text-zita-mocha">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Identity */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-zita-mocha mb-2">
                Publisher &amp; Domain
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm text-zita-espresso">
                <div>
                  <div className="text-zita-mocha text-xs mb-0.5">Publisher</div>
                  <div>{app.publisher || <span className="text-zita-mocha italic">Not provided</span>}</div>
                </div>
                <div>
                  <div className="text-zita-mocha text-xs mb-0.5">Website</div>
                  <div>
                    {app.website ? (
                      <a
                        href={app.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:underline inline-flex items-center gap-1"
                      >
                        {app.domain || app.website} <ExternalLink size={12} />
                      </a>
                    ) : (
                      <span className="text-zita-mocha italic">Not provided</span>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Requester */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-zita-mocha mb-2">
                Requested by
              </h3>
              <div className="flex items-center gap-2 text-sm text-zita-espresso">
                <UserIcon size={14} />
                <span>{app.requested_by_email || 'Unknown'}</span>
              </div>
              {app.request_reason && (
                <div className="mt-2 text-sm text-zita-mocha italic">
                  “{app.request_reason}”
                </div>
              )}
            </section>

            {/* Requested permissions */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-zita-mocha mb-2">
                Requested permissions
              </h3>
              {app.requested_permissions?.length ? (
                <ul className="list-disc list-inside text-sm text-zita-espresso space-y-1">
                  {app.requested_permissions.map((p) => (
                    <li key={p} data-testid={`tpa-permission-${p}`}>{p}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-zita-mocha italic">None specified</div>
              )}
              {app.permission_justification && (
                <div className="mt-2 text-xs text-zita-mocha">
                  Justification: {app.permission_justification}
                </div>
              )}
            </section>

            {/* Ari review */}
            {app.ari_review && (
              <section className="rounded-xl border border-zita-taupe/60 p-4 bg-zita-cream/40">
                <h3 className="text-xs uppercase tracking-wider text-zita-mocha mb-2 flex items-center gap-2">
                  <ShieldAlert size={14} /> Ari security review
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  <RiskBadge level={app.ari_review.risk_level} />
                  <Pill tone="zinc" testId={`ari-rec-${app.ari_review.recommendation}`}>
                    Recommends: {app.ari_review.recommendation.replace('_', ' ')}
                  </Pill>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <div className="text-zita-mocha text-xs mb-0.5">Publisher reputation</div>
                    <div className="text-zita-espresso">{app.ari_review.publisher_reputation || '—'}</div>
                  </div>
                  <div>
                    <div className="text-zita-mocha text-xs mb-0.5">Domain reputation</div>
                    <div className="text-zita-espresso">{app.ari_review.domain_reputation || '—'}</div>
                  </div>
                </div>
                {app.ari_review.suspicious_signals?.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-red-700 font-semibold mb-1">
                      Suspicious signals
                    </div>
                    <ul className="list-disc list-inside text-sm text-red-800 space-y-0.5">
                      {app.ari_review.suspicious_signals.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {app.ari_review.concerns?.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-amber-800 font-semibold mb-1">
                      Concerns
                    </div>
                    <ul className="list-disc list-inside text-sm text-amber-900 space-y-0.5">
                      {app.ari_review.concerns.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                <div className="text-sm text-zita-espresso">
                  <div className="text-zita-mocha text-xs mb-1">Reasoning</div>
                  <div>{app.ari_review.reasoning || '—'}</div>
                </div>
                <div className="text-[10px] text-zita-mocha mt-3">
                  Reviewed by {app.ari_review.reviewer_model} · Super Admin has final authority.
                </div>
              </section>
            )}

            {/* Info-request loop */}
            {app.info_request_message && (
              <section className="rounded-xl border border-blue-200 bg-blue-50/40 p-4">
                <h3 className="text-xs uppercase tracking-wider text-blue-800 mb-2">
                  Information request
                </h3>
                <div className="text-sm text-blue-900 whitespace-pre-line">
                  {app.info_request_message}
                </div>
                {app.info_response && (
                  <div className="mt-3">
                    <div className="text-xs text-blue-800 font-semibold mb-1">
                      User response
                    </div>
                    <div className="text-sm text-zita-espresso whitespace-pre-line">
                      {app.info_response}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Audit log */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-zita-mocha mb-2">
                Audit log
              </h3>
              {app.decision_log?.length ? (
                <ul className="space-y-2">
                  {app.decision_log.map((entry, i) => (
                    <li
                      key={i}
                      className="text-sm border border-zita-taupe/60 rounded-lg px-3 py-2"
                      data-testid={`tpa-log-${entry.action}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-zita-espresso capitalize">
                          {entry.action.replace('_', ' ')}
                        </div>
                        <div className="text-[11px] text-zita-mocha">
                          {new Date(entry.at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-xs text-zita-mocha">
                        by {entry.admin_username} · {entry.prior_status} → {entry.new_status}
                      </div>
                      {entry.reason && (
                        <div className="text-sm text-zita-espresso mt-1">
                          {entry.reason}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-zita-mocha italic">No decisions yet.</div>
              )}
            </section>

            {/* Actions */}
            {!app.is_recognized && (
              <section className="flex flex-wrap gap-2 border-t border-zita-taupe/60 pt-4">
                {['pending', 'info_requested'].includes(app.status) && (
                  <>
                    <button
                      type="button"
                      className="zita-btn-primary text-sm"
                      onClick={() => setAction('approve')}
                      data-testid="tpa-btn-approve"
                    >
                      <CheckCircle2 size={14} className="inline mr-1" /> Approve
                    </button>
                    <button
                      type="button"
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
                      onClick={() => setAction('reject')}
                      data-testid="tpa-btn-reject"
                    >
                      <XCircle size={14} className="inline mr-1" /> Reject
                    </button>
                    <button
                      type="button"
                      className="bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded text-sm"
                      onClick={() => setAction('block')}
                      data-testid="tpa-btn-block"
                    >
                      <Ban size={14} className="inline mr-1" /> Block
                    </button>
                    <button
                      type="button"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
                      onClick={() => setAction('request_info')}
                      data-testid="tpa-btn-request-info"
                    >
                      <MessageSquare size={14} className="inline mr-1" /> Request more info
                    </button>
                  </>
                )}
                {['rejected', 'blocked'].includes(app.status) && (
                  <button
                    type="button"
                    className="zita-btn-secondary text-sm"
                    onClick={() => setAction('reopen')}
                    data-testid="tpa-btn-reopen"
                  >
                    <RotateCcw size={14} className="inline mr-1" /> Reopen for review
                  </button>
                )}
              </section>
            )}
            {app.is_recognized && (
              <div className="text-xs text-zita-mocha italic flex items-center gap-2">
                <Info size={12} /> This app is on Zita&apos;s built-in approved-list and
                cannot be individually rejected or blocked from this screen.
              </div>
            )}
          </div>
        )}

        <DecisionDialog
          open={!!action}
          action={action}
          appName={app?.app_name || ''}
          onClose={() => setAction(null)}
          onSubmit={(reason) => decide(action, reason)}
        />
      </aside>
    </>
  );
};

// ---------------------------------------------------------------------------
// Main List
// ---------------------------------------------------------------------------

export const AdminThirdPartyApps = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [approvedList, setApprovedList] = useState([]);

  const load = () => {
    setLoading(true);
    const url = statusFilter === 'all'
      ? '/admin/third-party-apps'
      : `/admin/third-party-apps?status=${statusFilter}`;
    api.get(url)
      .then((r) => {
        setItems(r.data.items || []);
        setCounts(r.data.counts_by_status || {});
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]);

  useEffect(() => {
    api.get('/third-party-apps/approved-list')
      .then((r) => setApprovedList(r.data.approved_list || []))
      .catch(() => {});
  }, []);

  const tabs = useMemo(() => ([
    { key: 'pending', label: 'Pending' },
    { key: 'info_requested', label: 'Info requested' },
    { key: 'approved', label: 'Approved' },
    { key: 'approved_recognized', label: 'Recognized' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'blocked', label: 'Blocked' },
    { key: 'all', label: 'All' },
  ]), []);

  return (
    <div data-testid="admin-third-party-apps">
      <h1 className="font-serif text-3xl text-zita-espresso mb-2">
        Third-Party App Approvals
      </h1>
      <p className="text-sm text-zita-mocha mb-6 max-w-3xl">
        Recognized apps on Zita&apos;s approved-list connect immediately. Any unknown
        third-party app is reviewed by Ari and requires your explicit decision
        before it can access user data.
      </p>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="tpa-counts">
        {tabs.filter((t) => t.key !== 'all').map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatusFilter(t.key)}
            data-testid={`tpa-count-${t.key}`}
            className={`zita-card p-3 text-left transition ${
              statusFilter === t.key ? 'ring-2 ring-zita-cocoa' : 'hover:bg-zita-cream/60'
            }`}
          >
            <div className="text-[11px] uppercase tracking-wider text-zita-mocha">{t.label}</div>
            <div className="text-2xl font-serif text-zita-espresso">
              {counts[t.key] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatusFilter(t.key)}
            data-testid={`tpa-tab-${t.key}`}
            className={`text-sm px-3 py-1.5 rounded-full border ${
              statusFilter === t.key
                ? 'bg-zita-cocoa text-white border-zita-cocoa'
                : 'bg-white text-zita-mocha border-zita-taupe/60 hover:bg-zita-cream/60'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-zita-mocha">Loading…</div>
      ) : items.length === 0 ? (
        <div className="zita-card p-8 text-center text-zita-mocha" data-testid="tpa-empty">
          Nothing to review here.
        </div>
      ) : (
        <div className="space-y-2" data-testid="tpa-list">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => setOpenId(it.id)}
              data-testid={`tpa-row-${it.id}`}
              className="w-full text-left zita-card p-4 flex items-center gap-4 hover:bg-zita-cream/40 transition"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-serif text-lg text-zita-espresso truncate">
                    {it.app_name}
                  </div>
                  <StatusBadge status={it.status} />
                  {it.ari_review && <RiskBadge level={it.ari_review.risk_level} />}
                </div>
                <div className="text-xs text-zita-mocha truncate">
                  {it.publisher || 'Unknown publisher'} · {it.domain || it.website || '—'} · Requested by {it.requested_by_email}
                </div>
                {it.requested_permissions?.length > 0 && (
                  <div className="text-xs text-zita-mocha mt-1">
                    Wants: {it.requested_permissions.slice(0, 4).join(', ')}
                    {it.requested_permissions.length > 4 && ` +${it.requested_permissions.length - 4} more`}
                  </div>
                )}
              </div>
              <ChevronRight size={16} className="text-zita-mocha shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Approved-list footer */}
      <details className="mt-8" data-testid="tpa-approved-list">
        <summary className="text-sm text-zita-mocha cursor-pointer hover:text-zita-espresso">
          Zita approved-list ({approvedList.length} apps that skip this queue)
        </summary>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-3">
          {approvedList.map((a) => (
            <div key={a.source_key} className="text-xs border border-zita-taupe/60 rounded px-2 py-1.5">
              <div className="font-semibold text-zita-espresso">{a.name}</div>
              <div className="text-zita-mocha truncate">{a.publisher}</div>
              <div className="text-[10px] text-zita-mocha">{a.domain}</div>
            </div>
          ))}
        </div>
      </details>

      <DetailDrawer
        appId={openId}
        onClose={() => setOpenId(null)}
        onChanged={load}
      />
    </div>
  );
};

export default AdminThirdPartyApps;
